import { type ReactNode } from "react"
import styled, { css } from "styled-components"

import Modal from "@forento/ui/components/Modal"

import Button from "~/components/Button"
import { HelpIcon } from "~/components/Icon"
import { lightTextColor } from "~/utilities/styles"

interface Props {
	isOpen: boolean
	isElevated?: boolean
	width?: string | number
	onSubmit?(): void
	/** Called when the user presses Escape. */
	onClose?(): void
	children: ReactNode
}

const horizontalPadding = "clamp(16px, 4vw, 32px)"

export const DefaultModal: React.FC<Props> = ({ isOpen, isElevated, width, onSubmit, onClose, children }) => (
	<StyledModal width={width ?? 600} isOpen={isOpen} isElevated={isElevated} onSubmit={onSubmit} onClose={onClose}>
		{children}
	</StyledModal>
)

const StyledModal = styled(Modal)`
	border-radius: 24px;
	padding: 24px 0;
`

export const ModalTitle = styled.h2`
	font-weight: 600;
	font-size: 20px;
	color: #101828;
	padding: 8px ${horizontalPadding} 20px ${horizontalPadding};
`

export const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px ${horizontalPadding} 20px ${horizontalPadding};

	${ModalTitle} {
		padding: 0;
	}
`

export const ModalHelpCenter = styled(Button)`
	color: ${lightTextColor};
	font-size: 14px;
	display: flex;
	align-items: center;
	gap: 8px;
`

export const ModalHelpCenterIcon = styled(HelpIcon)`
	width: 16px;
	height: 16px;
`

export const ModalText = styled.div`
	color: ${lightTextColor};
	font-size: 14px;
	padding: 0 ${horizontalPadding} 20px ${horizontalPadding};

	ul,
	ol {
		margin-left: 1em;
	}
`

export const ModalTexts = styled(ModalText)`
	display: flex;
	flex-direction: column;
	gap: 1em;
`

export const ModalPadding = styled.div`
	padding: 0 ${horizontalPadding};
`

export const ModalContentPadding = styled.div`
	padding: 0 ${horizontalPadding} 20px ${horizontalPadding};
`

export const ModalInputs = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0 ${horizontalPadding} 20px ${horizontalPadding};
	overflow-y: auto;
`

export const ModalInputsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	> * {
		flex: 1 1 180px;
		min-width: 0;
	}
	gap: 16px;
`

export const ModalButtons = styled.div<{ $vertical?: boolean }>`
	display: flex;
	justify-content: flex-end;
	flex-wrap: wrap;
	padding: 0 ${horizontalPadding};
	gap: 12px;

	${props =>
		props.$vertical &&
		css`
			flex-direction: column;
		`}
`
